import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wallet, Calendar, Gift, CreditCard, Shield, PenTool as Tool, FileText, ArrowRight, Battery, Gauge, Scale,
  CheckCircle, Clock, FileCheck, Zap, Truck, Heart, ChevronRight, Star, Tag, Share2, Info, Smartphone, Award
} from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import SimulationForm from '../components/forms/SimulationForm';
import { MOTORCYCLE_MODELS } from '../constants/products';
import PayMoloForm from '../components/forms/PayMoloForm';
import toast from 'react-hot-toast';

const PayAsYouGo: React.FC = () => {
  const [showSimulation, setShowSimulation] = useState(false);
  const [showPayMoloForm, setShowPayMoloForm] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'payg' | 'paymolo'>('payg');
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Pay As You Go | Proxy Emobility';
  }, []);

  const features = [
    {
      icon: <Wallet className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Paiement Flexible",
      description: "Choisissez votre acompte et la durée de paiement qui vous convient"
    },
    {
      icon: <Calendar className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Premier Mois Gratuit",
      description: "Profitez d'un mois d'essai gratuit pour démarrer sereinement"
    },
    {
      icon: <Gift className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Pack Tout Inclus",
      description: "Assurance, maintenance et équipements de sécurité inclus"
    },
    {
      icon: <CreditCard className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Acompte Minimum",
      description: "Démarrez avec seulement 150 000 FCFA d'acompte"
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Assurance Complète",
      description: "RC + Tous Risques inclus pendant 12 mois"
    },
    {
      icon: <Tool className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Maintenance Incluse",
      description: "Entretien régulier et pièces d'usure"
    },
    {
      icon: <Gift className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Kit de Sécurité",
      description: "Casque et gilet réfléchissant offerts"
    },
    {
      icon: <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Immatriculation",
      description: "Carte grise et plaque incluses"
    }
  ];

  const payMoloFeatures = [
    {
      icon: <CheckCircle className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Simple et Rapide",
      description: "Processus de paiement simplifié et livraison rapide après paiement"
    },
    {
      icon: <Clock className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Flexibilité Totale",
      description: "Payez à votre rythme avant de venir récupérer votre moto"
    },
    {
      icon: <FileCheck className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Zéro Paperasse",
      description: "Documentation et immatriculation prises en charge"
    },
    {
      icon: <Truck className="w-8 h-8 sm:w-12 sm:h-12 text-accent-300" />,
      title: "Livraison Garantie",
      description: "Livraison assurée dès le paiement complet"
    }
  ];

  const payMoloAdvantages = [
    {
      icon: <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Tranquillité d'Esprit",
      description: "Payez sereinement avant de prendre possession de votre moto"
    },
    {
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Processus Accéléré",
      description: "Documentation et préparation en parallèle de votre paiement"
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary-600" />,
      title: "Sécurité Garantie",
      description: "Paiements sécurisés et traçables"
    }
  ];

  const handleShare = (modelId: string) => {
    const url = `${window.location.origin}/products/${modelId}`;
    if (navigator.share) {
      navigator.share({
        title: `Moto électrique ${MOTORCYCLE_MODELS.find(m => m.id === modelId)?.name}`,
        text: MOTORCYCLE_MODELS.find(m => m.id === modelId)?.description || '',
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Lien copié !');
    }
  };

  const handleOpenPayMolo = (modelId: string) => {
    setSelectedModel(modelId);
    setShowPayMoloForm(true);
  };

  const handleOpenSimulation = (modelId: string) => {
    setSelectedModel(modelId);
    setShowSimulation(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 to-primary-900 text-white py-12 sm:py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
              Pay As You Go
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed mb-6 sm:mb-8">
              Accédez à votre moto électrique avec notre solution de paiement flexible. 
              Choisissez votre modèle, personnalisez votre plan et commencez à rouler dès aujourd'hui.
            </p>
            <button
              onClick={() => setShowSimulation(true)}
              className="px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-accent-300 text-primary-900 rounded-lg sm:rounded-xl hover:bg-accent-400 transition-colors font-medium shadow-lg inline-flex items-center text-sm sm:text-base"
            >
              Simuler mon plan
              <ArrowRight size={18} className="ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-primary-50 to-white rounded-lg sm:rounded-xl p-6 sm:p-8 border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl">
                <div className="mb-4 sm:mb-6">{feature.icon}</div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Models Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              Nos Modèles
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Choisissez le modèle qui correspond à vos besoins et simulez votre plan de paiement personnalisé.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-8 sm:mb-12">
            <div className="inline-flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('payg')}
                className={`px-4 py-2 rounded-md text-sm sm:text-base font-medium transition-colors ${
                  activeTab === 'payg' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pay As You Go
              </button>
              <button
                onClick={() => setActiveTab('paymolo')}
                className={`px-4 py-2 rounded-md text-sm sm:text-base font-medium transition-colors ${
                  activeTab === 'paymolo' 
                    ? 'bg-primary-600 text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pay Molo
              </button>
            </div>
          </div>

          {/* New Product Card Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {MOTORCYCLE_MODELS.map((model) => (
              <div 
                key={model.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:border-primary-200 transition-all hover:shadow-xl group"
                onMouseEnter={() => setHoveredModel(model.id)}
                onMouseLeave={() => setHoveredModel(null)}
              >
                {/* Card Header with Image */}
                <div className="relative">
                  <div className="aspect-w-16 aspect-h-10 bg-gradient-to-br from-gray-50 to-white">
                    <img
                      src={model.image}
                      alt={model.name}
                      className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  
                  {/* Floating Price Tag */}
                  <div className="absolute top-3 right-3 bg-white rounded-full shadow-md px-3 py-1.5 flex items-center">
                    <Tag size={14} className="text-primary-600 mr-1" />
                    <span className="font-bold text-primary-600 text-sm">{formatCurrency(model.basePrice)}</span>
                  </div>
                  
                  {/* Floating Rating */}
                  <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          size={12} 
                          className={star <= model.rating ? "text-yellow-400 fill-current" : "text-gray-300"} 
                        />
                      ))}
                    </div>
                    <span className="ml-1 text-xs text-gray-700">{model.rating}</span>
                  </div>
                  
                  {/* Action Buttons - Only visible on hover */}
                  <div className={`absolute top-3 left-3 flex space-x-2 transition-opacity duration-300 ${hoveredModel === model.id ? 'opacity-100' : 'opacity-0'}`}>
                    <button
                      onClick={() => handleShare(model.id)}
                      className="p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
                      aria-label="Partager"
                    >
                      <Share2 size={14} className="text-gray-600" />
                    </button>
                    <Link
                      to={`/products/${model.id}`}
                      className="p-2 bg-white rounded-full shadow-md hover:bg-gray-50 transition-colors"
                      aria-label="Plus d'informations"
                    >
                      <Info size={14} className="text-gray-600" />
                    </Link>
                  </div>
                </div>
                
                {/* Card Content */}
                <div className="p-4 sm:p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">{model.name}</h3>
                    <div className="px-2 py-1 bg-primary-50 rounded-full text-xs text-primary-700 font-medium">
                      {model.usage}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2 text-sm">{model.description}</p>
                  
                  {/* Specs Grid */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="flex flex-col items-center text-center bg-gray-50 rounded-lg p-2">
                      <Battery size={16} className="text-primary-500 mb-1" />
                      <span className="text-xs font-medium text-gray-900">{model.specs.autonomie}</span>
                      <span className="text-xs text-gray-500">Autonomie</span>
                    </div>
                    <div className="flex flex-col items-center text-center bg-gray-50 rounded-lg p-2">
                      <Gauge size={16} className="text-primary-500 mb-1" />
                      <span className="text-xs font-medium text-gray-900">{model.specs.vitesse}</span>
                      <span className="text-xs text-gray-500">Vitesse</span>
                    </div>
                    <div className="flex flex-col items-center text-center bg-gray-50 rounded-lg p-2">
                      <Smartphone size={16} className="text-primary-500 mb-1" />
                      <span className="text-xs font-medium text-gray-900">App</span>
                      <span className="text-xs text-gray-500">Connectée</span>
                    </div>
                  </div>
                  
                  {/* Financing Options */}
                  <div className="bg-primary-50 rounded-lg p-3 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center">
                        <Award size={16} className="text-primary-600 mr-1" />
                        <span className="text-sm font-medium text-gray-900">Options de financement</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Mensualité à partir de</span>
                        <span className="text-sm font-bold text-primary-600">{formatCurrency(Math.round(model.basePrice / 24))}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Acompte minimum</span>
                        <span className="text-sm font-medium text-gray-900">150 000 FCFA</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {activeTab === 'payg' ? (
                      <>
                        <button
                          onClick={() => handleOpenSimulation(model.id)}
                          className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center"
                        >
                          Simuler PAYG
                          <ArrowRight size={14} className="ml-1" />
                        </button>
                        <Link
                          to={`/products/${model.id}`}
                          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center"
                        >
                          Voir détails
                          <ChevronRight size={14} className="ml-1" />
                        </Link>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleOpenPayMolo(model.id)}
                          className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center"
                        >
                          Pay Molo
                          <ArrowRight size={14} className="ml-1" />
                        </button>
                        <Link
                          to={`/products/${model.id}`}
                          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium flex items-center justify-center"
                        >
                          Voir détails
                          <ChevronRight size={14} className="ml-1" />
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              Tout Est Inclus
            </h2>
            <p className="text-base sm:text-lg text-gray-600">
              Notre offre Pay As You Go comprend tout ce dont vous avez besoin pour rouler en toute sérénité.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-primary-200 transition-all hover:shadow-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pay Molo Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
              Pay Molo - Payez en Toute Simplicité
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Une solution de paiement flexible qui vous permet de payer votre moto à votre rythme 
              avant de venir la récupérer. Simplicité, flexibilité et tranquillité d'esprit garanties.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-12 md:mb-16">
            {payMoloFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 sm:p-8 shadow-lg border border-primary-100 hover:border-primary-200 transition-all hover:shadow-xl group">
                <div className="mb-4 sm:mb-6 transform group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-white rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-12 mb-8 sm:mb-12 md:mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Comment ça marche ?
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold mr-3 sm:mr-4">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2">Choisissez votre moto</h4>
                      <p className="text-gray-600 text-sm sm:text-base">Sélectionnez le modèle qui correspond à vos besoins</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold mr-3 sm:mr-4">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2">Définissez votre plan</h4>
                      <p className="text-gray-600 text-sm sm:text-base">Choisissez le montant et la fréquence de vos paiements</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold mr-3 sm:mr-4">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2">Effectuez vos paiements</h4>
                      <p className="text-gray-600 text-sm sm:text-base">Payez à votre rythme via notre plateforme sécurisée</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold mr-3 sm:mr-4">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2">Récupérez votre moto</h4>
                      <p className="text-gray-600 text-sm sm:text-base">Une fois le paiement complet effectué, venez chercher votre moto</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {payMoloAdvantages.map((advantage, index) => (
                  <div key={index} className="bg-white p-4 sm:p-6 rounded-xl shadow-md hover:shadow-lg transition-all">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-3 sm:mr-4">
                        {advantage.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2">{advantage.title}</h4>
                        <p className="text-gray-600 text-sm sm:text-base">{advantage.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link 
              to="/contact"
              className="inline-flex items-center px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-primary-600 text-white font-medium rounded-lg sm:rounded-xl hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              Démarrer avec Pay Molo
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Simulation Modal */}
      <SimulationForm
        isOpen={showSimulation}
        onClose={() => setShowSimulation(false)}
      />

      {/* Pay Molo Form */}
      <PayMoloForm
        isOpen={showPayMoloForm}
        onClose={() => setShowPayMoloForm(false)}
        productName={selectedModel ? MOTORCYCLE_MODELS.find(m => m.id === selectedModel)?.name || '' : ''}
        productPrice={selectedModel ? MOTORCYCLE_MODELS.find(m => m.id === selectedModel)?.basePrice || 0 : 0}
      />
    </div>
  );
};

export default PayAsYouGo;